import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, Loader2, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";

const QBO_FAIL = {
  denied: "QuickBooks sign-in was cancelled or access was not allowed.",
  session: "QuickBooks sent you back to a different copy of the app than the one you started from. Start Connect from the same site whose address is set as the Redirect URI in Intuit.",
  expired: "The QuickBooks sign-in took longer than 10 minutes. Please try again.",
  token: "QuickBooks rejected the connection. Check that the Redirect URI in Intuit exactly matches this site's /api/qbo/oauth/callback and that production keys are saved.",
  intuit_error: "QuickBooks returned an error during sign-in. Please try again.",
};

export function useQboStatus(isAdmin) {
  const [st, setSt] = useState(null);
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const flag = params.get("qbo");
    if (flag) {
      if (flag === "connected") toast.success("QuickBooks connected");
      else if (flag === "disconnected") toast.info("QuickBooks was disconnected from the QuickBooks side. Reconnect it from the Admin panel to keep syncing.");
      else toast.error(QBO_FAIL[params.get("reason")] || "QuickBooks connection didn't finish. Try again from the Admin panel.", { duration: 15000 });
      params.delete("qbo");
      params.delete("reason");
      setParams(params, { replace: true });
    }
    if (isAdmin) api.get("/qbo/status").then((r) => setSt(r.data)).catch(() => setSt(null));
  }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps
  return st;
}

export default function QboActions({ ids, onDone }) {
  const [busy, setBusy] = useState(null);
  const run = async (kind) => {
    setBusy(kind);
    try {
      const { data } = await api.post(kind === "send" ? "/qbo/send" : "/qbo/pull-payments", { ids });
      const failed = data.results.filter((r) => !r.ok);
      const scans = data.results.reduce((s, r) => s + (r.scans_attached || 0), 0);
      if (kind === "send") toast.success(`${data.sent} of ${ids.length} invoice${ids.length === 1 ? "" : "s"} sent to QuickBooks${scans ? ` · ${scans} scan${scans === 1 ? "" : "s"} attached` : ""}`);
      else toast.success(data.results.length ? `${data.newly_paid.length} newly paid from QuickBooks (${data.results.length} checked)` : "None of these have been sent to QuickBooks yet");
      if (failed.length) toast.error(`${failed[0].number}: ${failed[0].error}${failed.length > 1 ? ` (+${failed.length - 1} more)` : ""}`);
      onDone();
    } catch (e) {
      toast.error(formatError(e));
    }
    setBusy(null);
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => run("send")} disabled={!!busy} data-testid="qbo-send-button" className="text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-400">
        {busy === "send" ? <Loader2 size={15} className="animate-spin" /> : <UploadCloud size={15} />} Send to QuickBooks
      </Button>
      <Button size="sm" variant="outline" onClick={() => run("pull")} disabled={!!busy} data-testid="qbo-pull-button" className="hover:bg-secondary hover:text-foreground">
        {busy === "pull" ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Pull payments
      </Button>
    </>
  );
}
